import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details 
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  #portalContainer = /** @type {HTMLElement | null} */ (null);
  #drawerContent = /** @type {HTMLElement | null} */ (null);
  #backdrop = /** @type {HTMLElement | null} */ (null);
  #originalParent = /** @type {HTMLElement | null} */ (null);
  #closeButtonHandler = /** @type {((event: Event) => void) | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#initializePortal();
    this.#setupCloseButtonListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    
    if (this.#closeButtonHandler) {
      document.removeEventListener('click', this.#closeButtonHandler);
      this.#closeButtonHandler = null;
    }
    
    this.#cleanupPortal();
  }

  /**
   * Initialize the portal container at body level
   */
  #initializePortal() {
    this.#drawerContent = this.querySelector('.menu-drawer');
    this.#backdrop = this.querySelector('.menu-drawer__backdrop');
    
    if (!this.#drawerContent) return;

    this.#originalParent = this.#drawerContent.parentElement;

    this.#portalContainer = document.getElementById('menu-drawer-portal');
    if (!this.#portalContainer) {
      this.#portalContainer = document.createElement('div');
      this.#portalContainer.id = 'menu-drawer-portal';
      this.#portalContainer.className = 'menu-drawer-portal';
      document.body.appendChild(this.#portalContainer);
    }
  }


  /**
   * Clean up portal container if empty
   */
  #cleanupPortal() {
    if (this.isOpen && this.#drawerContent?.parentElement === this.#portalContainer) {
      this.#moveFromPortal();
    }

    if (this.#portalContainer && this.#portalContainer.children.length === 0) {
      this.#portalContainer.remove();
      this.#portalContainer = null;
    }
  }

 
  /**
   * Move drawer content to portal (body level)
   */
  #moveToPortal() {
    if (!this.#drawerContent || !this.#portalContainer) return;

    if (this.#drawerContent && this.#drawerContent.parentElement !== this.#portalContainer) {
      this.#portalContainer.appendChild(this.#drawerContent);
    }
    if (this.#backdrop && this.#backdrop.parentElement !== this.#portalContainer) {
      this.#portalContainer.appendChild(this.#backdrop);
    }

    this.#portalContainer.classList.add('menu-open');

    this.#portalContainer.style.pointerEvents = 'auto';
  }


  #moveFromPortal() {
    if (!this.#drawerContent || !this.#originalParent) return;

    // Remove menu-open class from portal
    if (this.#portalContainer) {
      this.#portalContainer.classList.remove('menu-open');
      this.#portalContainer.style.pointerEvents = 'none';
    }

    if (this.#drawerContent && this.#drawerContent.parentElement === this.#portalContainer) {
      this.#originalParent.appendChild(this.#drawerContent);
    }
    if (this.#backdrop && this.#backdrop.parentElement === this.#portalContainer) {
      this.#originalParent.appendChild(this.#backdrop);
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#moveToPortal();
    }

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        const focusContainer = (details === this.refs.details && this.#portalContainer) 
          ? this.#portalContainer 
          : details;
        trapFocus(focusContainer);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        this.#moveFromPortal();
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        for (const detail of openDetails) {
          if (detail instanceof HTMLDetailsElement) {
            reset(detail);
          }
        }
      } else {
        setTimeout(() => {
          const focusContainer = this.#portalContainer && this.#drawerContent?.parentElement === this.#portalContainer
            ? this.#portalContainer
            : this.refs.details;
          trapFocus(focusContainer);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    for (const element of allAnimated) {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    }
  }

  /**
   * Setup event listeners for close buttons that work even when drawer is in portal
   */
  #setupCloseButtonListeners() {
    this.#closeButtonHandler = (event) => {
      if (!(event.target instanceof Element)) return;

      const closeButton = event.target.closest('.menu-drawer__close-button');
      if (!closeButton) return;

      const drawer = closeButton.closest('.menu-drawer');
      if (!drawer) return;

      const isInPortal = this.#portalContainer?.contains(drawer);
      const isInComponent = this.contains(drawer);

      if (isInPortal || isInComponent) {
        event.preventDefault();
        this.close();
      }
    };
    
    document.addEventListener('click', this.#closeButtonHandler);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
